<script setup lang="ts">
import { Loader2 } from 'lucide-vue-next';
import { Primitive, type PrimitiveProps } from 'radix-vue';
import { computed, type HTMLAttributes, useAttrs } from 'vue';
import { buttonVariants, type ButtonVariants } from '.';
import { cn } from '@/utils';

interface Props extends PrimitiveProps {
    variant?: ButtonVariants['variant'];
    size?: ButtonVariants['size'];
    class?: HTMLAttributes['class'];
    /**
     * A request raised by this button is in flight.
     *
     * Implies `disabled`. It used to only draw a spinner, which reads as "wait"
     * but does nothing to stop a second click — so every double-submittable
     * button in the app looked busy while happily posting again. A spinner is a
     * statement that the work is already happening; the button must behave like
     * it.
     */
    loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    as: 'button',
});

/**
 * Attributes are bound explicitly so `disabled` can be RESOLVED rather than
 * merged. Left to fallthrough, a caller's `:disabled="false"` sitting beside a
 * true `loading` would decide the attribute and re-open the button mid-request.
 */
defineOptions({ inheritAttrs: false });

const attrs = useAttrs();

// Only a native button has a `disabled` attribute; on an anchor it is invalid
// and, worse, inert — so `as-child`/`as="a"` callers keep today's behaviour.
const isNativeButton = computed(() => props.as === 'button' && !props.asChild);

/**
 * A bare `disabled` attribute (`<Button disabled>`) reaches `attrs` as an
 * empty string, which `Boolean()` reads as false — so it has to count as set.
 * Only an explicit `false`/`null`/`undefined` leaves the button enabled.
 */
const isDisabledAttr = (value: unknown): boolean => value === '' || (value !== false && value !== null && value !== undefined && value !== 'false');

const disabled = computed(() => (isNativeButton.value ? Boolean(props.loading) || isDisabledAttr(attrs.disabled) || undefined : undefined));
</script>

<template>
    <Primitive
        :as="as"
        :as-child="asChild"
        v-bind="attrs"
        :disabled="disabled"
        :class="cn(buttonVariants({ variant, size }), props.class, loading && 'min-w-10 opacity-100')"
    >
        <slot />
        <div v-if="loading" class="flex items-center justify-center">
            <Loader2 class="h-5 w-5 animate-spin" />
        </div>
    </Primitive>
</template>
